import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { getEntities as getTourCategories } from 'app/entities/tour-category/tour-category.reducer';
import { ITour } from 'app/shared/model/tour.model';
import { getEntities as getTours } from 'app/entities/tour/tour.reducer';
import { ITourCategory } from 'app/shared/model/tour-category.model';
import { getEntity, updateEntity, createEntity, reset } from './tour-category.reducer';

export const TourCategoryUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const account = useAppSelector(state => state.authentication.account);
  const tourCategories = useAppSelector(state => state.tourCategory.entities);
  const tours = useAppSelector(state => state.tour.entities);
  const tourCategoryEntity = useAppSelector(state => state.tourCategory.entity);
  const loading = useAppSelector(state => state.tourCategory.loading);
  const updating = useAppSelector(state => state.tourCategory.updating);
  const updateSuccess = useAppSelector(state => state.tourCategory.updateSuccess);

  const [formData, setFormData] = useState<any>({
    createdBy: account?.id,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/tour-category' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({
        createdBy: account?.id,
      });
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getTourCategories({}));
    dispatch(getTours({}));
  }, []);

  useEffect(() => {
    if (tourCategoryEntity) {
      setFormData({
        ...tourCategoryEntity,
        createdBy: tourCategoryEntity?.createdBy?.id,
        parent: tourCategoryEntity?.parent?.id,
        tours: tourCategoryEntity?.tours?.map(e => e.id.toString()),
      });
    }
  }, [tourCategoryEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleMultiSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.checked });
  };

  const renderMultiSelectValue = (selected: any[]) => {
    return selected?.join(', ') || '';
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };

    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }

    values.enabled = values.enabled === true;

    const entity = {
      ...tourCategoryEntity,
      ...values,
      createdBy: users.find(it => it.id.toString() === values.createdBy?.toString()),
      parent: tourCategories.find(it => it.id.toString() === values.parent?.toString()),
      tours: values.tours ? mapIdList(values.tours) : [],
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.tourCategory.home.createOrEditLabel">Create or edit a TourCategory</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
            {!isNew ? (
              <TextField
                id="tour-category-id"
                name="id"
                label={translate('global.field.id')}
                value={formData.id || ''}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            ) : null}
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourCategory.code')}
              id="tour-category-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <FormControlLabel
              control={
                <Switch
                  id="tour-category-enabled"
                  name="enabled"
                  data-cy="enabled"
                  checked={formData.enabled || false}
                  onChange={handleSwitchChange('enabled')}
                />
              }
              label={translate('jhipsterSampleApplicationApp.tourCategory.enabled')}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourCategory.icon')}
              id="tour-category-icon"
              name="icon"
              data-cy="icon"
              value={formData.icon || ''}
              onChange={handleChange('icon')}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="tour-category-parent-label">{translate('jhipsterSampleApplicationApp.tourCategory.parent')}</InputLabel>
              <Select
                labelId="tour-category-parent-label"
                id="tour-category-parent"
                name="parent"
                data-cy="parent"
                value={formData.parent || ''}
                label={translate('jhipsterSampleApplicationApp.tourCategory.parent')}
                onChange={handleSelectChange('parent')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {tourCategories
                  ? tourCategories.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="tour-category-tours-label">{translate('jhipsterSampleApplicationApp.tourCategory.tour')}</InputLabel>
              <Select
                labelId="tour-category-tours-label"
                id="tour-category-tours"
                data-cy="tours"
                multiple
                value={formData.tours || []}
                label={translate('jhipsterSampleApplicationApp.tourCategory.tour')}
                onChange={handleMultiSelectChange('tours')}
                renderValue={renderMultiSelectValue}
              >
                {tours
                  ? tours.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/tour-category"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TourCategoryUpdate;
